import { Box, Grid2, Paper, Typography } from '@mui/material'
import remarkGfm from 'remark-gfm'
import ReactMarkdown from 'react-markdown';
import FeedbackElements from '../shared/FeedbackElements';
import TimeAndDataSourcesFooter from '../shared/TimeAndDataSourcesFooter';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { createBookmark, removeBookmark, selectIfBookmarked } from '../../store/slices/bookmarksSlice';
import { selectIfFeedbackGiven, submitFeedback } from '../../store/slices/feedbackSlice';
import { useEffect, useState } from 'react';
import { MESSAGES } from '../../constants/Messages';
import { NavLink } from 'react-router-dom';
import { completedStreamingForEntry } from '../../store/slices/threadsSlice';
import rehypeRaw from 'rehype-raw';
import ChartContainer from '../charts/ChartContainer';
import { APP_ROUTES } from '../../constants/AppRoutes';


const CURSOR_BLOCK_MARKDOWN = `<span className='cursor_block'></span>`;

function AssistantResponse(
    { thread_id,
        isStreaming,
        entry_id,
        text,
        time,
        sources = {},
        plot,
        isFloatingChat = false,
        onTextStreamingFn }
        : {
            thread_id: string,
            isStreaming: boolean,
            entry_id: string,
            text: string,
            time: string,
            sources?: any,
            plot?: any,
            isFloatingChat?: boolean,
            onTextStreamingFn?: (value: boolean) => void
        }) {

    const dispatch = useDispatch<AppDispatch>();

    const isBookmarked = useSelector(selectIfBookmarked(thread_id, entry_id))
    const hasFeedback = useSelector(selectIfFeedbackGiven(entry_id));
    const [textContent, setTextContent] = useState(isStreaming ? '' : text);

    const hideVisual = isFloatingChat && plot !== null;

    useEffect(() => {
        let timeId = 0;
        if (hideVisual) {
            dispatch(completedStreamingForEntry({ thread_id: thread_id }))
            return;
        }
        if (isStreaming) {
            let i = 0;
            const stringResponse = text;
            const timeId = setInterval(() => {
                if (i === stringResponse.length)
                    setTextContent(stringResponse.slice(0, i))
                else
                    setTextContent(stringResponse.slice(0, i));
                i++;
                if (i > stringResponse.length) {
                    onTextStreamingFn && onTextStreamingFn(false)
                    clearInterval(timeId);
                    dispatch(completedStreamingForEntry({ thread_id: thread_id }))
                } else
                    onTextStreamingFn && onTextStreamingFn(true)
            }, 10);
        }
        return () => clearInterval(timeId);
    }, [onTextStreamingFn, dispatch, isStreaming, text, thread_id, hideVisual]);

    const handleBookmark = (value: boolean) => {
        if (isBookmarked) {
            dispatch(removeBookmark({
                thread_id,
                entry_id
            }))
        } else {
            dispatch(createBookmark({
                thread_id,
                entry_id
            }))
        }
    }

    const handleFeedback = (isThumbUp: boolean, comment?: string) => {
        dispatch(submitFeedback({
            rating: isThumbUp,
            comment: comment || '',
            entry_id: entry_id
        }))
    }

    return (
        <>
            <Grid2
                id={`e-${entry_id.replace('-', '')}`}
                sx={{
                    width: '100%',
                    background: '#18FEFE14',
                    boxSizing: 'border-box',
                    padding: isFloatingChat ? 3 : 4,
                    paddingLeft: isFloatingChat ? 5 : 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRadius: 1,
                }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        background: 'transparent',
                        display: 'flex',
                        textWrap: 'wrap'
                    }}>
                    <Typography variant='body1' component={'div'}
                        sx={{
                            width: '100%',
                            textWrap: 'wrap',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,

                            '& > *': { margin: 0, display: 'inline-block', maxWidth: '100%', },
                            '& > pre': { background: '#FFFFFF0D', padding: 1, borderLeft: '2px solid grey' },
                            '& > table': {
                                borderCollapse: 'collapse',
                                maxWidth: '100%',
                                overflowY: 'hidden',
                                overflowX: 'scroll',
                                '& th, & td': {
                                    padding: 1,
                                    border: '1px solid #d0dada',
                                },
                                '& thead tr, & tr:nth-of-type(even)': {
                                    background: '#daf3f3'
                                }
                            }
                        }}>
                        {
                            hideVisual ?
                                MESSAGES.CHAT_VISUALIZATION_HIDDEN
                                : <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                    {`${textContent}${isStreaming ? CURSOR_BLOCK_MARKDOWN : ''}`}
                                </ReactMarkdown>
                        }
                    </Typography>
                </Paper>
                {!hideVisual && !isStreaming && plot && <ChartContainer visual={plot}></ChartContainer>}
                {
                    (!isStreaming) && <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isFloatingChat ? 'center' : "flex-end",
                        flexWrap: 'wrap',
                        gap: 2,
                    }}>
                        <TimeAndDataSourcesFooter time={time}
                            category='response'
                            dataSources={sources}
                            hideDataSources={isFloatingChat}></TimeAndDataSourcesFooter>
                        {
                            !hideVisual ? <FeedbackElements
                                bookmarked={isBookmarked}
                                bookmarkFn={handleBookmark}
                                copyFn={() => navigator.clipboard.writeText(text)}
                                thumbUp={hasFeedback}
                                thumbFn={handleFeedback}></FeedbackElements>
                                : <Box sx={{
                                    display: 'grid',
                                    placeContent: 'center'
                                }}>
                                    <NavLink to={`${APP_ROUTES.ASK_GUARDIAN}/${thread_id}?entry_id=${entry_id}`}
                                        replace={true}
                                        style={{
                                            
                                            color: '#00A9B1',
                                            padding: 0,
                                            textDecoration: 'underline',
                                            lineHeight: 1,
                                        }}>View in Ask Guardian</NavLink>
                                </Box>
                        }
                    </Box>
                }
            </Grid2>
        </>
    )
}

export default AssistantResponse